import { createSelector } from "@reduxjs/toolkit";
import { memo, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { usePortalAnchor } from "@skilitics/designer-ui-components";

import { PROPERTY_PANEL_ANCHOR_ID } from "./selection/constants.ts";
import { NodePropertiesView, useSelectedNodeId } from "./selection/index.ts";
import { getComponentCreate } from "./shared";
import { getManifestSelector, nodeTypeIdParamSelector, useNautilusSelector } from "./state";
import type { DesignerComponentProps, DesignerNodeView } from "./types/index.ts";
import { useDesignerNodeComponentProps, type NodeDesignerProps } from "./useNodeDesignerComponentProps";

const getComponentSelector = createSelector(getManifestSelector, (getManifest) => getComponentCreate(getManifest));

const viewParamSelector = (_: unknown, { view }: { view?: DesignerNodeView }) => view;

const componentSelector = createSelector(
  [getComponentSelector, nodeTypeIdParamSelector, viewParamSelector],
  (getComponent, typeId, view) => getComponent(typeId, { view }),
);

const iconSelector = createSelector(
  [getManifestSelector, nodeTypeIdParamSelector],
  (getManifest, typeId) => getManifest(typeId).designer.icon,
);

const componentAndIconSelector = createSelector([componentSelector, iconSelector], (component, icon) => ({
  component,
  icon,
}));

export const NodeDesigner = memo(
  // eslint-disable-next-line @typescript-eslint/no-shadow -- function named after the const for devtools
  function NodeDesigner({
    node,
    onConfigChange,
    onRemove,
    view,
    index,
    mode,
    children,
    ownsProperties = false,
  }: NodeDesignerProps) {
    const { id, upstreamTemplateId } = node;
    const componentProps = useDesignerNodeComponentProps({ node, onConfigChange, onRemove, view, index, mode });

    const [selectedNodeId] = useSelectedNodeId();
    const isNodeSelected = selectedNodeId === id;
    const shouldRenderPortal = isNodeSelected && (view === "details" || ownsProperties);

    const propertyPanelAnchor = usePortalAnchor(PROPERTY_PANEL_ANCHOR_ID);

    // null while rendering a just-deleted node (before it unmounts)
    if (!componentProps) {
      return null;
    }

    return (
      <>
        <NodeComponent key={id} componentProps={componentProps}>
          {children}
        </NodeComponent>
        {shouldRenderPortal &&
          propertyPanelAnchor &&
          createPortal(
            <NodePropertiesView key={node.id} node={node}>
              <NodeComponent
                key={id}
                componentProps={{ ...componentProps, view: "properties" }}
                upstreamTemplateId={upstreamTemplateId}
              />
            </NodePropertiesView>,
            propertyPanelAnchor,
          )}
      </>
    );
  },
);

interface NodeComponentProps {
  componentProps: DesignerComponentProps<any>;
  upstreamTemplateId?: number;
  children?: ReactNode;
}

const NodeComponent = memo(
  // eslint-disable-next-line @typescript-eslint/no-shadow -- function named after the const for devtools
  function NodeComponent({ componentProps, upstreamTemplateId, children }: NodeComponentProps) {
    const { id, typeId, title, view } = componentProps;
    const { component: Component, icon } = useNautilusSelector((state) =>
      componentAndIconSelector(state, { typeId, view }),
    );

    if (!Component && !icon) {
      throw new Error(`No "${view}" view or icon registered for node designer with typeId: ${typeId}.`);
    }

    const value = Component ? (
      <Component key={id} {...componentProps}>
        {children}
      </Component>
    ) : (
      icon
    );
    if (window.interactDebugMode && typeId !== "module") {
      // ignore module as breaks layout (as do some others but have to start somewhere)
      return (
        <div style={{ border: "1px solid red", padding: "1px" }}>
          <span style={{ fontSize: "0.6rem", opacity: 0.5 }}>
            &lt;{typeId} id={id} {title && `title=${title}`} {view && `view=${view}`}&gt;
          </span>
          {upstreamTemplateId && "@"}
          {value}
          <span style={{ fontSize: "0.6rem", opacity: 0.5 }}>&lt;/{typeId}&gt;</span>
        </div>
      );
    }

    return value;
  },
);
